import {
  resolveCuratedImage,
  type BrowserRef,
  type NewsItem,
} from 'volto-ministerio-publico-federal/components/View/CapaView/helpers';

/** Uma foto da galeria, como o `ObjectListWidget` grava. */
export interface GaleriaItem {
  /** Gerado pelo `ObjectListWidget`; serve de `key` estável na lista. */
  '@id'?: string;
  /** Widget `image`: ver `resolveCuratedImage` sobre os formatos aceitos. */
  image?: string | BrowserRef[];
  alt?: string;
  caption?: string;
  /**
   * Crédito só desta foto. Em branco, a foto herda o `creditoPadrao` do bloco.
   *
   * Não é lido do objeto `Image`: o widget `image` grava só a URL, sem UID nem
   * atributos de brain, e este projeto não consulta o catálogo por caminho
   * (ver a nota de `Blocks/Campanhas/useCampaigns.ts` sobre `path.query` sem
   * VirtualHostMonster). O behavior `ministerio.publico.federal.imagem_credito`
   * guarda o crédito no próprio arquivo, para quem abre a imagem direto e para
   * listagens futuras.
   */
  credito?: string;
}

/** Dados do bloco `mpfGaleriaImagens`. */
export interface GaleriaData {
  title?: string;
  items?: GaleriaItem[];
  /**
   * Crédito de toda a galeria. Uma cobertura costuma ser de um fotógrafo só —
   * preencher aqui evita repetir a mesma linha foto a foto. Cada item pode
   * sobrescrever com o seu.
   */
  creditoPadrao?: string;
  colunasGrade?: number;
  mostrarCredito?: boolean;
}

/** View-model de uma foto, já numerada e na ordem de exibição. */
export interface GaleriaFoto {
  key: string;
  /** Posição em `items` — é o que o lápis passa ao editor. */
  sourceIndex: number;
  /** Posição na galeria já filtrada, base 0. */
  indice: number;
  /** O mesmo, formatado com dois dígitos (`01`). */
  numero: string;
  alt: string;
  legenda?: string;
  credito?: string;
  item: GaleriaItem;
}

/** Título padrão do cartão, quando o editor não escreve um. */
export const GALERIA_TITULO = 'Galeria de imagens';

/**
 * Escalas do Plone por papel.
 *
 * `large` (800px de largura) no palco, e não `preview` (400px): a caixa do
 * palco mede 650px no desktop, então `preview` era esticado em ~1,6x — upscale
 * visível, e pior ainda em tela retina. As escalas do Plone limitam só a
 * LARGURA (`mini 200:65536`, `preview 400:65536`, `large 800:65536`), então
 * uma foto em retrato não perde resolução por ser vertical.
 */
export const ESCALA_PALCO = 'large';
export const ESCALA_AMPLIADA = 'large';
export const ESCALA_MINIATURA = 'mini';

/**
 * Miniaturas empilhadas ao lado do palco. A quarta posição da coluna é o botão
 * `+N`, daí a trilha ocupar 4 células mas mostrar 3 fotos.
 */
export const TRILHA_MINIATURAS = 3;

/** Com até isto, a trilha dá conta da galeria inteira e o `+N` não aparece. */
export const GALERIA_SEM_EXCEDENTE = TRILHA_MINIATURAS + 1;

/** Acima deste total a grade pagina; abaixo, sai inteira numa página só. */
export const GRADE_LIMIAR_PAGINACAO = 24;
export const GRADE_POR_PAGINA = 12;

export const GRADE_COLUNAS_PADRAO = 4;

/** Numeração do design: sempre dois dígitos. */
export const doisDigitos = (valor: number): string =>
  String(valor).padStart(2, '0');

/** Número da foto (base 0 → `01`). */
export const numero = (indice: number): string => doisDigitos(indice + 1);

/** Contador do cabeçalho, no formato `03 / 12`. */
export const contador = (indice: number, total: number): string =>
  `${numero(indice)} / ${doisDigitos(total)}`;

export const toGaleriaFotos = (
  items?: GaleriaItem[],
  creditoPadrao?: string,
): GaleriaFoto[] =>
  (items ?? [])
    // O índice de origem é capturado ANTES do filtro: o lápis precisa da
    // posição em `items`, não da posição na lista exibida.
    .map((item, sourceIndex) => ({ item, sourceIndex }))
    // Item sem imagem não vira foto: um quadro vazio no meio da galeria é só
    // um buraco, e a numeração passaria a mentir sobre o total.
    .filter(({ item }) => !!resolveCuratedImage(item.image, ESCALA_PALCO))
    .map(({ item, sourceIndex }, indice) => ({
      key: item['@id'] ?? `foto-${sourceIndex}`,
      sourceIndex,
      indice,
      numero: numero(indice),
      alt: item.alt || item.caption || '',
      legenda: item.caption || undefined,
      // O da foto vence; sem ele, o da galeria. `||` e não `??`: string
      // vazia (campo tocado e apagado no editor) precisa cair no padrão.
      credito: item.credito || creditoPadrao || undefined,
      item,
    }));

/**
 * As `quantidade` fotos seguintes à atual, circularmente.
 *
 * Nunca repete a foto em cartaz: com 2 fotos e `quantidade: 3`, devolve uma só.
 * É o que faz a trilha se ajustar sozinha a galerias pequenas, sem um segundo
 * caminho de código para o caso "4 fotos ou menos".
 */
export const proximas = (
  fotos: GaleriaFoto[],
  indice: number,
  quantidade = TRILHA_MINIATURAS,
): GaleriaFoto[] => {
  const total = fotos.length;
  const passos = Math.min(quantidade, Math.max(total - 1, 0));
  return Array.from(
    { length: passos },
    (_, i) => fotos[(indice + i + 1) % total],
  );
};

/** Quantas fotos sobram fora da trilha — o `N` do botão `+N`. */
export const excedente = (total: number): number =>
  Math.max(total - GALERIA_SEM_EXCEDENTE, 0);

/** Navegação circular do palco. */
export const proxima = (indice: number, total: number): number =>
  total > 0 ? (indice + 1) % total : 0;

export const anterior = (indice: number, total: number): number =>
  total > 0 ? (indice - 1 + total) % total : 0;

/** A grade só pagina acima de `GRADE_LIMIAR_PAGINACAO`. */
export const gradePagina = (total: number): boolean =>
  total > GRADE_LIMIAR_PAGINACAO;

export const totalDePaginas = (total: number): number =>
  gradePagina(total) ? Math.ceil(total / GRADE_POR_PAGINA) : 1;

export const fatiaDaPagina = (
  fotos: GaleriaFoto[],
  pagina: number,
): GaleriaFoto[] =>
  gradePagina(fotos.length)
    ? fotos.slice(pagina * GRADE_POR_PAGINA, (pagina + 1) * GRADE_POR_PAGINA)
    : fotos;

/** Props de imagem da foto, prontas para o `<Image>` do Volto. */
export const getGaleriaImageProps = (
  foto: GaleriaFoto,
  escala: string = ESCALA_PALCO,
): { item: NewsItem } | { src: string } | null =>
  resolveCuratedImage(foto.item.image, escala);
